import React, { useState, useEffect, useMemo } from 'react';
import LeavePeriodDisplay from '../components/Leave/LeavePeriodDisplay';
import { SessionPeriodChip } from '../components/UI/SessionPeriodChip';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffAPI } from '../services/api';
import SmartLeaveApplicationModal from '../components/SmartLeaveApplicationModal';
import WorkerLeaveModal from '../components/WorkerLeaveModal';
import ApprovalTimeline from '../components/Leave/ApprovalTimeline';
import ApprovalStepper from '../components/Leave/ApprovalStepper';
import Modal from '../components/UI/Modal';
import ConfirmModal from '../components/UI/ConfirmModal';
import { 
  Calendar, 
  Plus, 
  PenSquare,
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye,
  Edit,
  Trash2,
  Download,
  FileText,
  TrendingUp,
  X,
  Wallet,
  CalendarDays,
  CalendarX,
  Heart,
  Baby,
  Thermometer,
  Hospital,
  Plane,
  AlertTriangle,
  Home,
  MapPinned,
  Hourglass,
  Gem,
  Grid3X3, 
  List, 
  GraduationCap,
  Shield,
  Sun,
  Tag,
  ClipboardList,
  ArrowLeftRight,
} from 'lucide-react';
import { formatDate, formatDateTime, formatDateTimeCompact, formatDateShort, getYearInSingapore, getTodayInSingaporeTimezone } from '../utils/dateUtils';
import { formatLeaveDays, formatLeaveDaysEmployee, pluralizeDays } from '../utils/numberUtils';
import { usePersistence } from '../hooks/usePersistence';
import { useAuth } from '../contexts/AuthContext';
import {
  canDeleteLeave,
  canCancelLeave,
  canRequestLeaveExtension,
  resolveLeaveStatus,
  getCancelActionLabel,
  isFullyApproved,
} from '../utils/leavePermissions';
import { shouldHideLeavePageCompassionateCard, isCompassionateLeaveType, WFH_REASONS } from '../constants/leaveTypes';
import { StatusBadge } from '../components/UI/StatusBadge';
import { InfoBadge } from '../components/UI/InfoBadge';
import {
  getAppliedLeaveTypeName,
  getDisplayLeaveTypeName,
  getDisplayTotalDays as resolveDisplayTotalDays,
  getLeavePeriodDisplayChips,
  getOilCreditedDays,
  hasCrossTypeConsumption,
  getNextAccrualDate,
  formatAccrualDate,
  formatOilCreditDaysLabel,
  getOilCreditDaysClassName,
  buildOilExpiryHint,
} from '../utils/leaveDisplayUtils';
import ConsumptionBreakdown from '../components/Leave/ConsumptionBreakdown';
import HomeLeaveConsumptionNote from '../components/Leave/HomeLeaveConsumptionNote';
import OilClaimWorkDates from '../components/Leave/OilClaimWorkDates';
import { OverseasTravelDeclarationSummary } from '../components/Leave/OverseasTravelDeclarationFields';
import { CountBadge } from '../components/UI/CountBadge';
import { FilterButton } from '../components/UI/FilterButton';
import { Pagination } from '../components/UI/Pagination';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import PillSelect from '../components/UI/PillSelect';
import PillToggle from '../components/UI/PillToggle';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import LeaveTransactionsView from '../components/Leave/LeaveTransactionsView';
import toast from '../utils/toast';
import { useAnnualLeaveYearEndPlan } from '../hooks/useLeaveData';
import AnnualLeavePlanningCallout from '../components/Leave/AnnualLeavePlanningCallout';
import AnnualLeaveYearEndBalance from '../components/Leave/AnnualLeaveYearEndBalance';

// Secondary indicator for Home Leave / Urgent Leave flight ticket workflow.
// Shown alongside the primary StatusBadge when the leave is approved but
// the flight ticket lifecycle hasn't concluded.
// Uses StatusBadge with statuses registered in statusConfig.js.
const FlightTicketIndicator = ({ leave }) => {
  const lt = (leave.leave_type?.leave_type_name || leave.leave_type_name || '').toLowerCase();
  if (lt !== 'home leave' && lt !== 'urgent leave') return null;

  const leaveStatus = leave?.leave_status || leave?.status;
  if (leaveStatus !== 'approved') return null;

  const verif = leave.flight_ticket_verification;
  if (verif?.status === 'approved') return null;

  let ticketStatus;
  if (verif?.status === 'rejected') {
    ticketStatus = 'ticket_rejected';
  } else if (leave.flight_details_submitted_at) {
    ticketStatus = 'ticket_under_review';
  } else {
    ticketStatus = 'ticket_required';
  }

  return (
    <div className="mt-1">
      <StatusBadge status={ticketStatus} />
    </div>
  );
};

// Helper function for progress bar width calculation
const calculateProgressBarWidth = (balance, total, remainingPercentage) => {
  // All leave types (including Annual Leave) use remaining percentage
  // This shows what percentage of total available days remain (including carry-forward)
  return isFinite(remainingPercentage) ? Math.min(Math.max(remainingPercentage, 0), 100) : 0;
};

/**
 * Resolves course name and location from either dedicated DB fields or by
 * parsing the legacy composite leave_reason string ("Course: X (Y)").
 */
const getCourseInfo = (leave) => {
  if (leave.course_name || leave.course_location) {
    return { courseName: leave.course_name, courseLocation: leave.course_location };
  }
  if (!leave.leave_reason) return null;
  const withLocation = leave.leave_reason.match(/^Course:\s*(.+)\s+\(([^)]+)\)$/);
  if (withLocation) return { courseName: withLocation[1].trim(), courseLocation: withLocation[2].trim() };
  const nameOnly = leave.leave_reason.match(/^Course:\s*(.+)$/);
  if (nameOnly) return { courseName: nameOnly[1].trim(), courseLocation: null };
  return null;
};

/**
 * Resolves the human-readable WFH reason label.
 * Handles both new records (leave_reason populated) and legacy records
 * where only the wfh_reason dropdown key was stored.
 */
const getWfhReasonLabel = (leave) => {
  if (leave.wfh_reason) {
    if (leave.wfh_reason === 'others') {
      return leave.wfh_other_reason || leave.leave_reason || 'Others';
    }
    const match = WFH_REASONS.find(r => r.value === leave.wfh_reason);
    return match?.label || leave.wfh_reason;
  }
  return leave.leave_reason || null;
};

const Leave = () => {
  // Get user from auth context for service month calculations
  const { user } = useAuth();

  // Check if user is a system account (excluded from probation messaging)
  const isSystemAccount = React.useCallback((u) => {
    return ['A0001', 'S0000'].includes(u?.staff_id) 
           || u?.employment_classification === 'system';
  }, []);

  // Calculate completed months of service (MOM-compliant: >= 3 months = entitled)
  const getCompletedServiceMonths = React.useCallback((employmentStartDate) => {
    if (!employmentStartDate) return 0;
    const start = new Date(employmentStartDate);
    const now = new Date();
    const months = (now.getFullYear() - start.getFullYear()) * 12 
                   + (now.getMonth() - start.getMonth());
    // Only count completed months (day of month must have passed)
    return now.getDate() >= start.getDate() ? months : months - 1;
  }, []);

  const getLeaveTypeIcon = (leaveTypeName) => {
    const name = leaveTypeName?.toLowerCase() || '';
    if (name.includes('urgent leave')) return { icon: AlertTriangle, color: 'orange' };
    if (name.includes('home leave') || name.includes('home-leave')) return { icon: Plane, color: 'sky' };
    if (name.includes('annual') || name.includes('vacation')) return { icon: Sun, color: 'blue' };
    if (name.includes('outpatient') || (name.includes('sick') && !name.includes('hospitalisation'))) return { icon: Thermometer, color: 'amber' };
    if (name.includes('hospitalisation')) return { icon: Hospital, color: 'red' };
    if (name.includes('maternity')) return { icon: Baby, color: 'pink' };
    if (name.includes('paternity')) return { icon: Baby, color: 'purple' };
    if (name.includes('shared parental')) return { icon: Baby, color: 'fuchsia' };
    if (name.includes('extended') && name.includes('childcare')) return { icon: Baby, color: 'emerald' };
    if (name.includes('childcare')) return { icon: Baby, color: 'green' };
    if (name.includes('marriage')) return { icon: Gem, color: 'rose' };
    if (name.includes('compassionate') || name.includes('bereavement')) return { icon: Heart, color: 'gray' };
    if (name.includes('unpaid')) return { icon: CalendarX, color: 'slate' };
    if (name === 'off in lieu' || name.includes('oil')) return { icon: Hourglass, color: 'teal' };
    if (name.includes('external activity') || name.includes('external')) return { icon: MapPinned, color: 'cyan' };
    if (name.includes('work from home') || name.includes('wfh')) return { icon: Home, color: 'indigo' };
    if (name === 'on course') return { icon: GraduationCap, color: 'violet' };
    if (name.includes('national service')) return { icon: Shield, color: 'lime' };
    return { icon: CalendarDays, color: 'stone' };
  };

  const getLeaveTypeColorClasses = (color) => ({
    bg: color === 'amber' ? 'bg-amber-100' : color === 'blue' ? 'bg-blue-100' : color === 'red' ? 'bg-red-100' : color === 'pink' ? 'bg-pink-100' : color === 'purple' ? 'bg-purple-100' : color === 'orange' ? 'bg-orange-100' : color === 'green' ? 'bg-green-100' : color === 'cyan' ? 'bg-cyan-100' : color === 'teal' ? 'bg-teal-100' : color === 'slate' ? 'bg-slate-100' : color === 'indigo' ? 'bg-indigo-100' : color === 'rose' ? 'bg-rose-100' : color === 'sky' ? 'bg-sky-100' : color === 'fuchsia' ? 'bg-fuchsia-100' : color === 'emerald' ? 'bg-emerald-100' : color === 'violet' ? 'bg-violet-100' : color === 'lime' ? 'bg-lime-100' : color === 'stone' ? 'bg-stone-100' : 'bg-gray-100',
    text: color === 'amber' ? 'text-amber-600' : color === 'blue' ? 'text-blue-600' : color === 'red' ? 'text-red-600' : color === 'pink' ? 'text-pink-600' : color === 'purple' ? 'text-purple-600' : color === 'orange' ? 'text-orange-600' : color === 'green' ? 'text-green-600' : color === 'cyan' ? 'text-cyan-600' : color === 'teal' ? 'text-teal-600' : color === 'slate' ? 'text-slate-600' : color === 'indigo' ? 'text-indigo-600' : color === 'rose' ? 'text-rose-600' : color === 'sky' ? 'text-sky-600' : color === 'fuchsia' ? 'text-fuchsia-600' : color === 'emerald' ? 'text-emerald-600' : color === 'violet' ? 'text-violet-600' : color === 'lime' ? 'text-lime-600' : color === 'stone' ? 'text-stone-600' : 'text-gray-600',
  });

  // State management
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedLeaveType, setSelectedLeaveType] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'delete'|'cancel', leave }
  const [cancellationReason, setCancellationReason] = useState('');
  const [showWorkerLeaveModal, setShowWorkerLeaveModal] = useState(false);
  const [workerLeaveType, setWorkerLeaveType] = useState(null); // 'home_leave' | 'annual_leave' | 'urgent_leave'
  const [selectedWorkerLeave, setSelectedWorkerLeave] = useState(null);
  const [workerLeaveInitialDates, setWorkerLeaveInitialDates] = useState(null);
  const { data: yearEndPlan } = useAnnualLeaveYearEndPlan();
  
  // View mode state with responsive detection
  // Compute default based on screen size if no saved preference exists
  const getDefaultViewMode = () => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('leave-applications-viewMode');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed === 'grid' || parsed === 'list') return parsed;
        } catch (e) {
          // Invalid saved value, compute default
        }
      }
      // No saved preference - compute based on screen size
      return window.innerWidth < 768 ? 'grid' : 'list';
    }
    return 'grid'; // SSR fallback
  };
  
  const [viewMode, setViewMode] = usePersistence('leave-applications-viewMode', getDefaultViewMode());

  // Leave page view: 'applications' (default — submitted leave history) vs
  // 'transactions' (bank-statement-style ledger of credits & debits per
  // leave-type account).  Persisted across visits because employees who
  // prefer the statement view tend to come back to it repeatedly.
  const [leaveTabMode, setLeaveTabMode] = usePersistence('leave-page-tab-mode', 'applications');

  const queryClient = useQueryClient();

  // Today's date in Singapore timezone — recomputed on tab focus to handle overnight sessions
  const [todayStr, setTodayStr] = useState(() => getTodayInSingaporeTimezone());

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const fresh = getTodayInSingaporeTimezone();
        setTodayStr(prev => prev !== fresh ? fresh : prev);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Fetch user's eligible leave types and eligibility metadata
  const { 
    data: eligibilityData,
    isLoading: leaveTypesLoading,
    error: leaveTypesError 
  } = useQuery({
    queryKey: ['leave', 'available-types'],
    queryFn: async () => {
      const response = await staffAPI.getAvailableLeaveTypes();
      const leaveTypes = response.data?.available_leave_types || [];
      
      // Deduplicate leave types by ID (in case backend returns duplicates)
      const uniqueLeaveTypes = Array.from(
        new Map(leaveTypes.map(type => [type.id, type])).values()
      );
      
      return {
        leaveTypes: uniqueLeaveTypes,
        recommendations: response.data?.recommendations || {},
        eligibilityDetails: response.data?.eligibility_details || {},
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - leave eligibility can change
  });
  const availableLeaveTypes = eligibilityData?.leaveTypes || [];
  const shouldUseUnpaidForAnnual = eligibilityData?.recommendations?.should_use_unpaid_for_annual || false;
  const annualLeaveEligibilityReason = eligibilityData?.eligibilityDetails?.annual_leave?.reason || '';
  const activeOverseasLeave = eligibilityData?.eligibilityDetails?.home_leave?.active_overseas_leave || null;

  // Fetch leave applications - using personal view endpoint
  const pageSize = 20;
  const { 
    data: leaveApplicationsData, 
    isLoading: applicationsLoading 
  } = useQuery({
    queryKey: ['leave', 'my-applications', { 
      search: searchTerm, 
      status: selectedStatus, 
      leave_type: selectedLeaveType,
      year: selectedYear,
      page: currentPage 
    }],
    queryFn: async () => {
      const params = {
        page: currentPage,
        page_size: pageSize,
        ordering: '-leave_start_date',
        include_drafts: true,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(selectedLeaveType && { leave_type: selectedLeaveType }),
        ...(selectedYear && { start_date_after: `${selectedYear}-01-01`, start_date_before: `${selectedYear}-12-31` })
      };
      const response = await staffAPI.getMyLeaveApplications(params);
      const results = response.data?.results || response.data || [];
      const count = response.data?.count ?? results.length;
      return { results, count };
    },
  });
  const leaveApplications = leaveApplicationsData?.results || [];
  const totalApplicationsCount = leaveApplicationsData?.count || 0;
  const totalPages = Math.ceil(totalApplicationsCount / pageSize);

  // Fetch ALL applications for the selected year (no pagination) when the
  // employee opens the Transactions / statement view.  We need the full
  // year so the bank-statement ledger reconciles correctly against the
  // current balance.  Disabled until the user actually toggles into the
  // transactions view to keep the Applications view fast.
  const txYear = selectedYear || new Date().getFullYear();
  const {
    data: transactionApplications = [],
    isLoading: transactionAppsLoading,
  } = useQuery({
    queryKey: ['leave', 'my-applications-statement', { year: txYear }],
    enabled: leaveTabMode === 'transactions',
    queryFn: async () => {
      const yearParams = {
        page: 1,
        page_size: 500,
        ordering: 'leave_start_date',
        include_drafts: false,
        end_date_after: `${txYear}-01-01`,
        start_date_before: `${txYear}-12-31`,
      };
      // OIL credits are claim-driven. A Claim: Off in Lieu may have
      // leave_start_date outside the statement year while oil_dates /
      // oil_credited_days still belong on this year's Off in Lieu ledger
      // (e.g. late-Dec work credited, used the following year). Pull
      // approved claims separately and merge when they touch the year.
      const oilClaimParams = {
        page: 1,
        page_size: 200,
        ordering: '-leave_applied_at',
        include_drafts: false,
        leave_type_name_exact: 'Claim: Off in Lieu',
        status_in: 'approved,auto_approved',
      };
      const [yearResp, oilResp] = await Promise.all([
        staffAPI.getMyLeaveApplications(yearParams),
        staffAPI.getMyLeaveApplications(oilClaimParams),
      ]);
      const yearApps = yearResp.data?.results || yearResp.data || [];
      const oilClaims = oilResp.data?.results || oilResp.data || [];

      const touchesStatementYear = (app) => {
        const inRange = (value) => {
          if (!value) return false;
          const ymd = String(value).match(/^(\d{4})/);
          return ymd ? Number(ymd[1]) === txYear : false;
        };
        if (inRange(app.leave_start_date) || inRange(app.leave_applied_at) || inRange(app.oil_claim_date)) {
          return true;
        }
        const oilDates = Array.isArray(app.oil_dates) ? app.oil_dates : [];
        return oilDates.some((entry) => inRange(entry?.work_date || entry?.date));
      };

      const byId = new Map();
      yearApps.forEach((app) => {
        if (app?.id != null) byId.set(app.id, app);
      });
      oilClaims.forEach((app) => {
        if (app?.id == null || byId.has(app.id)) return;
        if (touchesStatementYear(app)) byId.set(app.id, app);
      });
      return Array.from(byId.values());
    },
    staleTime: 60 * 1000,
  });

  // Fetch leave balances.
  // Returns { filtered, all }:
  //   - filtered → Balances cards (hides exhausted OIL, etc.)
  //   - all      → Transactions ledger lookups (must keep exhausted OIL so
  //                entitled/used history still reconciles; card filtering
  //                must never zero-out the statement account)
  const {
    data: leaveBalancesPayload,
    isLoading: balancesLoading,
  } = useQuery({
    queryKey: ['leave', 'balances'],
    queryFn: async () => {
      const response = await staffAPI.getMyLeaveBalances();
      const balances = response.data?.results || response.data || [];

      let filteredBalances = balances.filter(balance => {
        const leaveTypeName = balance.leave_type?.leave_type_name || '';
        const remainingDays = Number(balance.remaining_days ?? balance.remaining_balance ?? 0);
        const entitledDays = Number(balance.entitled_days ?? balance.total_entitlement ?? 0);

        // Exclude special types that don't have balance tracking (they show in leave application dropdown only)
        const specialNoBalanceTypes = ['Claim: Off in Lieu', 'External Activity', 'Work From Home', 'National Service Leave', 'On Course', 'Urgent Leave'];
        if (specialNoBalanceTypes.includes(leaveTypeName)) {
          return false;
        }

        // Hide "Off in Lieu" (usage) when balance is 0 - no point showing an exhausted OIL card
        if (leaveTypeName === 'Off in Lieu' && remainingDays <= 0) {
          return false;
        }

        // ALWAYS show sick leave types (even if 0 balance - probation case)
        // Exception: advisors have no sick leave entitlement at all
        if (leaveTypeName.includes('Sick') || leaveTypeName.includes('Hospitalisation')) {
          if (user?.employment_classification === 'advisor') {
            return false;
          }
          return true;
        }

        // Always keep Unpaid Leave in the data layer -- render-time logic
        // (displayBalances memo) decides whether to show AL or UL card,
        // never both, based on the eligibility API.
        if (leaveTypeName === 'Unpaid Leave') {
          return true;
        }

        // Compassionate leave: hide before first use (bereavement UX sensitivity)
        // Shows once used so employees can track remaining allocation, including when exhausted
        if (shouldHideLeavePageCompassionateCard(balance)) return false;

        // Hide leave types that the employee is not entitled to yet
        // Also handle NaN case from null values
        if ((isNaN(remainingDays) || remainingDays === 0) && (isNaN(entitledDays) || entitledDays === 0)) {
          return false;
        }

        return true; // Keep other eligible leave types
      });

      // Dedup safety net: if backend returns multiple balances for the same
      // leave type name (e.g. overlapping [all] + [staff] records), keep only
      // the first occurrence to prevent duplicate balance cards.
      const seenLeaveTypes = new Set();
      filteredBalances = filteredBalances.filter(balance => {
        const name = balance.leave_type?.leave_type_name || '';
        if (seenLeaveTypes.has(name)) return false;
        seenLeaveTypes.add(name);
        return true;
      });

      // Ensure Unpaid Leave record exists so the render-time either/or filter
      // can show it when needed. If the backend didn't return one, inject a synthetic card.
      const hasUnpaidLeave = filteredBalances.some(b => b.leave_type?.leave_type_name === 'Unpaid Leave');
      if (!hasUnpaidLeave) {
        filteredBalances.push({
          id: 'unpaid-leave-auto',
          leave_type: { id: 'unpaid-leave', leave_type_name: 'Unpaid Leave' },
          remaining_days: null,
          entitled_days: null,
          used_days: 0,
          is_work_arrangement: false,
          is_status_only: false
        });
      }

      // Define leave type sort order.
      // Home Leave sits immediately after Annual Leave for workers — it's the
      // second-most-relevant balance for them (30 cal days / calendar year,
      // MOM-aligned reset on 1 Jan). Hidden automatically for non-workers by
      // the entitled==0 && remaining==0 filter above.
      const leaveTypeOrder = {
        'Annual Leave': 1,
        'Home Leave': 1.5,
        'Outpatient Sick Leave': 2,
        'Hospitalisation Leave': 3,
        'Maternity Leave': 4,
        'Paternity Leave': 5,
        'Shared Parental Leave': 6,
        'Childcare Leave': 7,
        'Marriage Leave': 8,
        'Compassionate Leave (Immediate Family)': 9,
        'Compassionate Leave (Grandparent)': 9.5,
        'Unpaid Leave': 99,
      };

      // Sort filtered balances by defined order
      filteredBalances.sort((a, b) => {
        const nameA = a.leave_type?.leave_type_name || '';
        const nameB = b.leave_type?.leave_type_name || '';
        
        const orderA = leaveTypeOrder[nameA] || 999;
        const orderB = leaveTypeOrder[nameB] || 999;
        
        return orderA - orderB;
      });

      return { filtered: filteredBalances, all: balances };
    },
  });
  const leaveBalances = leaveBalancesPayload?.filtered || [];
  const {
    data: transactionBalancesAll = [],
    isLoading: transactionBalancesLoading,
  } = useQuery({
    queryKey: ['leave', 'balances', 'statement', txYear],
    enabled: leaveTabMode === 'transactions',
    queryFn: async () => {
      const response = await staffAPI.getMyLeaveBalances({ year: txYear });
      return response.data?.results || response.data || [];
    },
    staleTime: 60 * 1000,
  });
  // Smart filter: Hide "Off in Lieu" (usage) from the leave application dropdown.
  // Employees don't apply for OIL directly — when they apply for Annual Leave,
  // the system automatically consumes OIL first (oldest expiry first) before AL.
  //
  // EXCEPTION: when Annual Leave is suppressed (probation, insufficient service),
  // the auto-consumption surface does not exist, so a PH that falls on a Saturday
  // during probation would otherwise credit OIL the employee can never use.
  // Expose OIL directly in that case — the backend already returns it in the
  // eligibility payload when `should_use_unpaid_for_annual` is true and a
  // positive OIL balance exists.
  const filteredLeaveTypes = useMemo(() => {
    return availableLeaveTypes.filter(type => {
      const name = type.leave_type_name || type.name || '';
      
      // Always show "Claim: Off in Lieu" - employee can always submit claims
      if (name === 'Claim: Off in Lieu') return true;
      
      // Hide "Off in Lieu" (usage) only when Annual Leave is available to
      // auto-consume it. When AL is suppressed, fall through and show it.
      if (name === 'Off in Lieu' && !shouldUseUnpaidForAnnual) return false;
      
      // Show all other leave types
      return true;
    });
  }, [availableLeaveTypes, shouldUseUnpaidForAnnual]);

  // Either/or rule: show Annual Leave card OR Unpaid Leave card, never both.
  // The eligibility API is the single source of truth for which to display.
  const displayBalances = useMemo(() => {
    return leaveBalances.filter(balance => {
      const name = balance.leave_type?.leave_type_name || '';
      if (shouldUseUnpaidForAnnual) {
        if (name === 'Annual Leave') return false;
      } else {
        if (name === 'Unpaid Leave') return false;
      }
      return true;
    });
  }, [leaveBalances, shouldUseUnpaidForAnnual]);

  // Accounts to surface in the Transactions (bank-statement) view.
  //
  // Mental model: the statement must document EVERY leave type the employee
  // earned or used this year — not just the ones with a live balance card.
  // We therefore start from displayBalances (the balance-tracked accounts the
  // employee already sees on the Balances cards) and then fold in any leave
  // type that saw activity during the selected year (CCL, SPL, PL, ML, OIL,
  // Compassionate, etc.), even if its current balance is zero or its card is
  // hidden. Exhausted / event-based history is still meaningful in a ledger.
  //
  // Unpaid Leave is excluded — it has no balance to ledger against (it's a
  // salary deduction, surfaced inside the Annual Leave spillover instead).
  const transactionAccounts = useMemo(() => {
    const accounts = transactionBalancesAll.filter(b => {
      const name = b.leave_type?.leave_type_name || '';
      return name !== 'Unpaid Leave'
        && name !== 'Claim: Off in Lieu'
        && b.id != null;
    });
    const present = new Set(
      accounts.map(a => a.leave_type?.leave_type_name).filter(Boolean)
    );

    // Every leave type touched by an application this year: the type applied
    // for, plus any cross-type consumption (e.g. an Annual Leave request that
    // drew partly from Off in Lieu).
    const activityNames = new Set();
    (transactionApplications || []).forEach((app) => {
      const appliedName = app.leave_type?.leave_type_name || app.leave_type_name || '';
      if (appliedName) {
        // "Claim: Off in Lieu" is a submission workflow, not a balance — its
        // credited days belong to the "Off in Lieu" account.
        if (appliedName.toLowerCase() === 'claim: off in lieu') {
          activityNames.add('Off in Lieu');
        } else {
          activityNames.add(appliedName);
        }
      }
      (app.leave_consumption_proposal?.items || []).forEach((it) => {
        if (it?.leave_type) activityNames.add(it.leave_type);
      });
    });

    activityNames.forEach((name) => {
      if (!name || name === 'Unpaid Leave' || present.has(name)) return;
      // Prefer the real balance record from the unfiltered API payload
      // (carries entitlement / remaining). Card-filtered leaveBalances hides
      // exhausted OIL — using it here previously fabricated a zeroed
      // synthetic account and erased all credits from the statement.
      const real = transactionBalancesAll.find(
        b => b.leave_type?.leave_type_name === name
      );
      if (real) {
        accounts.push(real);
      } else {
        accounts.push({
          id: `synthetic-${name}`,
          leave_type: { leave_type_name: name },
          entitled_days: 0,
          accrued_days: 0,
          carried_forward_days: 0,
          remaining_days: 0,
          total_available_days: 0,
        });
      }
      present.add(name);
    });

    // Event-driven entitlements the employee is currently ELIGIBLE for but
    // hasn't used yet (e.g. Childcare Leave unlocked by a birth certificate,
    // Maternity / Paternity / Shared Parental / Marriage Leave). The
    // eligibility API already gates these to eligible-only, so their presence
    // here means "the grant happened" — and the grant itself is a meaningful
    // ledger entry the employee should see, even at zero usage. Work
    // arrangements / status-only / unlimited types (WFH, On Course, NS) carry
    // no balance to ledger and are skipped.
    (availableLeaveTypes || []).forEach((lt) => {
      const name = lt.name || lt.leave_type_name || '';
      if (!name || name === 'Unpaid Leave' || name === 'Annual Leave' || present.has(name)) return;
      if (lt.is_work_arrangement || lt.is_status_only || lt.is_unlimited || lt.is_event_triggered) return;
      if (lt.remaining_balance == null) return; // no balance concept
      // Bereavement sensitivity: Compassionate Leave is intentionally hidden
      // until first use (mirrors the Balances tab). Once used, it surfaces via
      // the activity path above, so it's safe to skip here.
      if (isCompassionateLeaveType(lt.id)) return;

      const real = transactionBalancesAll.find(
        b => b.leave_type?.leave_type_name === name
      );
      if (real) {
        accounts.push(real);
      } else {
        const entitled = Number(lt.remaining_balance) || 0;
        accounts.push({
          id: `eligible-${name}`,
          leave_type: { leave_type_name: name },
          entitled_days: entitled,
          accrued_days: 0,
          carried_forward_days: 0,
          remaining_days: entitled,
          total_available_days: entitled,
        });
      }
      present.add(name);
    });

    return accounts;
  }, [transactionBalancesAll, transactionApplications, availableLeaveTypes]);

  // Create leave application mutation
  // NOTE: error toasts are handled by SmartLeaveApplicationModal's catch block
  // which has detailed 400-status handling. Avoid duplicate toasts here.
  const createLeaveMutation = useMutation({
    mutationFn: (data) => staffAPI.createLeaveApplication(data),
    onSuccess: (_res, variables) => {
      const start = variables?.leave_start_date || variables?.start_date;
      const end = variables?.leave_end_date || variables?.end_date;
      const typeName = variables?.leave_type_name || variables?.leave_type?.leave_type_name;
      const range = start && end
        ? (start === end ? formatDate(start) : `${formatDate(start)} – ${formatDate(end)}`)
        : null;
      toast.success(
        range
          ? `Leave application submitted${typeName ? ` (${typeName})` : ''} for ${range}`
          : 'Leave application submitted successfully!'
      );
      queryClient.invalidateQueries(['leave']);
      const startYear = start ? Number(String(start).slice(0, 4)) : null;
      if (Number.isFinite(startYear)) {
        setSelectedYear(startYear);
        setCurrentPage(1);
      }
      setShowApplicationModal(false);
      setEditingLeave(null);
    },
  });

  // Update leave application mutation
  // NOTE: error toasts are handled by SmartLeaveApplicationModal's catch block.
  const updateLeaveMutation = useMutation({
    mutationFn: ({ id, data, useFile, file }) => {
      if (useFile && file) {
        return staffAPI.updateLeaveApplicationWithFile(id, data, file);
      }
      return staffAPI.updateLeaveApplication(id, data);
    },
    onSuccess: (_res, variables) => {
      const data = variables?.data || variables || {};
      const start = data.leave_start_date || data.start_date;
      const end = data.leave_end_date || data.end_date;
      const range = start && end
        ? (start === end ? formatDate(start) : `${formatDate(start)} – ${formatDate(end)}`)
        : null;
      toast.success(
        range
          ? `Leave application updated for ${range}`
          : 'Leave application updated successfully!'
      );
      queryClient.invalidateQueries(['leave']);
      const startYear = start ? Number(String(start).slice(0, 4)) : null;
      if (Number.isFinite(startYear)) {
        setSelectedYear(startYear);
        setCurrentPage(1);
      }
      setShowApplicationModal(false);
      setEditingLeave(null);
    },
  });

  // Delete leave application mutation
  const deleteLeaveMutation = useMutation({
    mutationFn: (id) => staffAPI.deleteLeaveApplication(id),
    onSuccess: () => {
      toast.success('Leave application deleted successfully!');
      queryClient.invalidateQueries(['leave']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete leave application');
    }
  });

  // Cancel/withdraw leave application mutation.
  // Same endpoint + terminal status ('cancelled') for both; only the wording
  // differs: an approved leave is "cancelled" (balance restored), a not-yet-
  // approved request is "withdrawn" (nothing was consumed to restore).
  const cancelLeaveMutation = useMutation({
    mutationFn: ({ id, data }) => staffAPI.cancelLeaveApplication(id, data),
    onSuccess: (_data, variables) => {
      if (variables?.wasApproved) {
        toast.success('Leave cancelled successfully! Balance restored.');
      } else {
        toast.success('Leave application withdrawn successfully!');
      }
      queryClient.invalidateQueries(['leave']);
      queryClient.invalidateQueries({ queryKey: ['pendingDocApprovalsSummary'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to cancel leave application');
    }
  });

  // Handle form submission — returns a Promise so the modal can await it
  // and keep isSubmitting=true until the HTTP round-trip completes.
  const handleSubmitLeave = async (formData, isDraft, draftApplicationId = null) => {
    const editingId = editingLeave?.id || editingLeave?.pk || editingLeave?.application_id || draftApplicationId || null;

    const hasFile = !!formData.leave_medical_cert_file;
    if (editingId) {
      if (hasFile && staffAPI.updateLeaveApplicationWithFile) {
        await updateLeaveMutation.mutateAsync({
          id: editingId,
          data: formData,
          useFile: true,
          file: formData.leave_medical_cert_file
        });
      } else {
        await updateLeaveMutation.mutateAsync({ id: editingId, data: formData });
      }
    } else {
      await createLeaveMutation.mutateAsync(formData);
    }
  };

  useEffect(() => {
    if (window.location.hash !== '#annual-leave-planning') return;
    if (!yearEndPlan?.visible) return;
    document.getElementById('annual-leave-planning')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [yearEndPlan?.visible]);

  // Handle edit leave
  const handleEditLeave = (leave) => {
    setEditingLeave(leave);
    setShowApplicationModal(true);
  };

  // Handle delete leave — opens confirmation modal
  const handleDeleteLeave = (leave) => {
    setConfirmAction({ type: 'delete', leave });
  };

  // Handle cancel leave (for approved/submitted leaves) — opens confirmation modal
  const handleCancelLeave = (leave) => {
    setConfirmAction({ type: 'cancel', leave });
  };

  // Execute confirmed action
  const handleConfirmAction = () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'delete') {
      deleteLeaveMutation.mutate(confirmAction.leave.id, {
        onSettled: () => setConfirmAction(null),
      });
    } else if (confirmAction.type === 'cancel') {
      cancelLeaveMutation.mutate(
        {
          id: confirmAction.leave.id,
          data: { cancellation_reason: cancellationReason },
          wasApproved: isFullyApproved(confirmAction.leave),
        },
        { onSettled: () => { setConfirmAction(null); setCancellationReason(''); } },
      );
    }
  };

  // Handle view leave details
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);

  const handleViewDetails = (leave) => {
    const ltName = (leave.leave_type?.leave_type_name || leave.leave_type_name || '').toLowerCase();
    const isWorker = user?.employment_classification === 'worker';

    // Worker leave types with multi-level approval tracking (only for workers)
    if ((ltName === 'home leave' || ltName === 'annual leave' || ltName === 'urgent leave') && isWorker) {
      setWorkerLeaveType(ltName === 'home leave' ? 'home_leave' : ltName === 'urgent leave' ? 'urgent_leave' : 'annual_leave');
      setSelectedWorkerLeave(leave);
      setShowWorkerLeaveModal(true);
      return;
    }

    // Staff and other leave types use standard details modal
    setSelectedLeave(leave);
    setShowDetailsModal(true);
  };

  const handleOpenActiveOverseasLeave = async () => {
    const activeLeaveId = activeOverseasLeave?.leave_id;
    if (!activeLeaveId) return;

    setShowApplicationModal(false);
    const cachedLeave = leaveApplications.find(
      application => String(application.id) === String(activeLeaveId)
    );

    if (cachedLeave) {
      toast.info('Opening your active leave so you can request an extension.');
      handleViewDetails(cachedLeave);
      return;
    }

    const loadingToastId = toast.loading('Opening your active leave…');
    try {
      const response = await staffAPI.getLeaveApplication(activeLeaveId);
      toast.dismiss(loadingToastId);
      toast.info('Opening your active leave so you can request an extension.');
      handleViewDetails(response.data);
    } catch (error) {
      toast.dismiss(loadingToastId);
      toast.error(
        error?.response?.data?.error
        || 'Could not open your active leave. Refresh the page and try again.'
      );
    }
  };

  const handleSwitchToWorkerLeave = async (leaveTypeName) => {
    const leaveType = leaveTypeName === 'home leave'
      ? 'home_leave'
      : leaveTypeName === 'urgent leave'
        ? 'urgent_leave'
        : 'annual_leave';
    const isOverseasLeaveType = leaveType === 'home_leave' || leaveType === 'urgent_leave';
    const activeLeaveId = activeOverseasLeave?.leave_id;

    if (isOverseasLeaveType && activeLeaveId) {
      await handleOpenActiveOverseasLeave();
      return;
    }

    setShowApplicationModal(false);
    setWorkerLeaveType(leaveType);
    setSelectedWorkerLeave(null);
    setWorkerLeaveInitialDates(
      isOverseasLeaveType ? { start: '', end: '' } : null
    );
    setShowWorkerLeaveModal(true);
  };

  // Note: resolveLeaveStatus is now imported from leavePermissions.js

  // Note: getDisplayLeaveTypeName and getOilCreditedDays imported from leaveDisplayUtils.js

  // Compute day count if backend field is absent; includes both endpoints inclusive
  const computeDaysCount = (leave) => {
    // Home Leave / Urgent Leave: proposal total is the calendar trip.
    // Do not use it for AL — a stale locked proposal can be 0.5 short
    // of leave_total_days when Saturday was AM-trimmed as a half day.
    const appliedName = (
      leave?.leave_type?.leave_type_name
      || leave?.leave_type_name
      || ''
    ).trim();
    if (
      (appliedName === 'Home Leave' || appliedName === 'Urgent Leave')
      && leave?.leave_consumption_proposal?.total_days
    ) {
      return Number(leave.leave_consumption_proposal.total_days);
    }
    if (leave?.leave_total_days || leave?.total_days || leave?.leave_days_count) {
      // If any backend-provided counts exist, prefer those elsewhere
      return Number(leave.leave_total_days || leave.total_days || leave.leave_days_count);
    }
    const start = leave?.leave_start_date ? new Date(leave.leave_start_date) : null;
    const end = leave?.leave_end_date ? new Date(leave.leave_end_date) : start;
    if (!start || Number.isNaN(start.getTime()) || !end || Number.isNaN(end.getTime())) return 0;
    // Inclusive days between start and end
    let days = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
    // Respect AM/PM half-day indicators when available (non-sick logic mirrors modal)
    if (leave.leave_start_time_period === 'PM') days -= 0.5;
    if (leave.leave_end_time_period === 'AM') days -= 0.5;
    return days;
  };

  const getDisplayTotalDays = (leave) =>
    resolveDisplayTotalDays(leave, computeDaysCount(leave));

  const resolveAppliedDate = (leave) => {
    return leave?.leave_applied_at || leave?.submitted_at || leave?.applied_at || leave?.created_at || leave?.created || null;
  };

  // Render grid view card for a leave application
  const renderLeaveCard = (leave) => {
    const status = resolveLeaveStatus(leave);
    const displayTypeName = getDisplayLeaveTypeName(leave);
    const appliedTypeName = getAppliedLeaveTypeName(leave);
    const wasRedirectedToUnpaid = appliedTypeName === 'Annual Leave' && displayTypeName === 'Unpaid Leave';
    const leaveTypeName = (leave.leave_type?.leave_type_name || leave.leave_type_name || '').toLowerCase();
    const isSickLeave = leaveTypeName.includes('outpatient') && leaveTypeName.includes('sick') || leaveTypeName.includes('hospitalisation');
    const isVerifiedSick = isSickLeave && status === 'approved';
    const oilCredited = getOilCreditedDays(leave);
    const days = oilCredited != null ? oilCredited : getDisplayTotalDays(leave);
    const isOilClaim = leaveTypeName === 'claim: off in lieu';
    const isExternalActivity = leaveTypeName === 'external activity';
    const isOnCourse = leaveTypeName === 'on course';
    const isWFH = leaveTypeName.includes('work from home') || leaveTypeName.includes('wfh');
    const { icon: CardLeaveIcon, color: cardColor } = getLeaveTypeIcon(displayTypeName);
    const cardColorClasses = getLeaveTypeColorClasses(cardColor);

    const canDelete = canDeleteLeave(leave);
    const canCancel = canCancelLeave(leave, todayStr);
    const canRequestExtension = canRequestLeaveExtension(leave, todayStr);
    const isDraft = status === 'draft' || status === 'draft_saved';
    const canEdit = isDraft;
    const cancelVerb = getCancelActionLabel(leave); // 'Withdraw' | 'Cancel'
    const hasFooterActions = canEdit || canDelete || canCancel || canRequestExtension;

    const openDetails = () => handleViewDetails(leave);
    const handleCardKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openDetails();
      }
    };
    const stopCardOpen = (e) => {
      e.stopPropagation();
    };

    return (
      <div
        key={leave.id}
        className="bg-white border border-gray-200 rounded-2xl p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        role="button"
        tabIndex={0}
        onClick={openDetails}
        onKeyDown={handleCardKeyDown}
        aria-label={`View details for ${wasRedirectedToUnpaid ? 'Annual Leave' : displayTypeName}`}
      >
        {/* Card Content - grows to fill space */}
        <div className="flex-1 min-w-0">
          {/* Header row: Leave Type + Status */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${cardColorClasses.bg} flex-shrink-0`}>
                  <CardLeaveIcon className={`w-3.5 h-3.5 ${cardColorClasses.text}`} />
                </span>
                {wasRedirectedToUnpaid ? 'Annual Leave' : displayTypeName}
              </h3>
              {wasRedirectedToUnpaid && (
                <p className="text-xs text-amber-600 mt-0.5">Deducted as Unpaid Leave (insufficient AL balance)</p>
              )}
              {isExternalActivity && (
                <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                  {leave.activity_type && (
                    <div className="flex items-center gap-1 line-clamp-1">
                      <Tag className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span>{leave.activity_type}</span>
                    </div>
                  )}
                  {leave.activity_location && (
                    <div className="flex items-center gap-1 line-clamp-1">
                      <MapPinned className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span>{leave.activity_location}</span>
                    </div>
                  )}
                  {!leave.activity_type && !leave.activity_location && leave.leave_reason && (
                    <div className="line-clamp-2">{leave.leave_reason}</div>
                  )}
                </div>
              )}
              {isOnCourse && (() => {
                const info = getCourseInfo(leave);
                if (!info) return null;
                return (
                  <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                    {info.courseName && (
                      <div className="flex items-center gap-1 line-clamp-1">
                        <GraduationCap className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span>{info.courseName}</span>
                      </div>
                    )}
                    {info.courseLocation && (
                      <div className="flex items-center gap-1 line-clamp-1">
                        <MapPinned className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span>{info.courseLocation}</span>
                      </div>
                    )}
                  </div>
                );
              })()}
              {isWFH && (() => {
                const wfhLabel = getWfhReasonLabel(leave);
                if (!wfhLabel) return null;
                return (
                  <div className="text-sm text-gray-500 mt-1">
                    <div className="flex items-center gap-1 line-clamp-1">
                      <Home className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span>{wfhLabel}</span>
                    </div>
                  </div>
                );
              })()}
              {!isExternalActivity && !isOnCourse && !isWFH && leave.leave_reason && (
                <div className="text-sm text-gray-500 mt-1 truncate">{leave.leave_reason}</div>
              )}
            </div>
            <div className="flex flex-col items-end flex-shrink-0">
              <StatusBadge status={status} {...(isVerifiedSick ? { label: 'Verified' } : {})} />
              <FlightTicketIndicator leave={leave} />
            </div>
          </div>

          {/* Dates row */}
          <div className="flex items-center gap-2 text-sm text-gray-900 mb-2">
            <Calendar className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              {leave.leave_start_date === leave.leave_end_date ? (
                <div className="flex items-center gap-1">
                  <span>{formatDate(leave.leave_start_date)}</span>
                  {days === 0.5 && (
                    <SessionPeriodChip session={leave.leave_start_time_period} />
                  )}
                </div>
              ) : (
                <div className="flex items-center flex-wrap gap-1">
                  {(() => {
                    const startYear = getYearInSingapore(leave.leave_start_date);
                    const endYear = getYearInSingapore(leave.leave_end_date);
                    const sameYear = startYear === endYear;
                    const displayChips = getLeavePeriodDisplayChips(leave);
                    const startChip = displayChips.start;
                    const endChip = displayChips.end;
                    
                    return (
                      <>
                        <span className="font-medium">
                          {sameYear ? formatDateShort(leave.leave_start_date) : formatDate(leave.leave_start_date)}
                        </span>
                        <SessionPeriodChip session={startChip} />
                        <span className="text-gray-400">—</span>
                        <span className="font-medium">{formatDate(leave.leave_end_date)}</span>
                        <SessionPeriodChip session={endChip} />
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Duration */}
          <div className="flex items-start text-sm text-gray-600 mb-2">
            <Clock className={`w-4 h-4 mr-2 flex-shrink-0 mt-0.5 ${isOilClaim ? (status === 'rejected' || status === 'cancelled' ? 'text-gray-400' : 'text-teal-500') : 'text-orange-500'}`} />
            <div className="flex items-center gap-2 flex-wrap">
              <span className={isOilClaim ? getOilCreditDaysClassName(status) : ''}>
                {isOilClaim
                  ? formatOilCreditDaysLabel(days, status)
                  : `${formatLeaveDays(days)} ${pluralizeDays(days)}`}
              </span>
              {hasCrossTypeConsumption(leave, displayTypeName) && (
                <ConsumptionBreakdown proposal={leave.leave_consumption_proposal} />
              )}
            </div>
          </div>

          {/* Meta row: Submitted/Approved dates with time */}
          <div className="space-y-1">
            {leave.leave_applied_at && (
              <div className="flex items-center text-xs text-gray-500">
                <FileText className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                <span>Applied: {formatDateTimeCompact(leave.leave_applied_at)}</span>
              </div>
            )}
            {resolveLeaveStatus(leave) === 'cancelled' && leave.cancelled_at ? (
              <div className="flex items-center text-xs text-gray-500">
                <XCircle className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                <span>Cancelled: {formatDateTimeCompact(leave.cancelled_at)}</span>
              </div>
            ) : leave.leave_approved_at && resolveLeaveStatus(leave) !== 'cancelled' ? (
              <div className="flex items-center text-xs text-gray-500">
                <CheckCircle className="w-3 h-3 mr-1 text-green-500 flex-shrink-0" />
                <span>Approved: {formatDateTimeCompact(leave.leave_approved_at)}</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Real actions only — View is the whole-card tap (DS mobile card rules).
            gap-3 on the shell keeps ≥12px above this strip when content is full. */}
        {hasFooterActions && (
          <div
            className="card-action-footer flex-nowrap"
            onClick={stopCardOpen}
            onKeyDown={stopCardOpen}
          >
            {canRequestExtension && (
              <button
                type="button"
                onClick={openDetails}
                className="btn btn-primary btn-sm"
                aria-label={`Request an extension for ${displayTypeName}`}
              >
                <Plus className="h-4 w-4" />
                Request Extension
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => handleEditLeave(leave)}
                className="icon-button"
                aria-label="Edit leave"
                title="Edit Leave"
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => handleDeleteLeave(leave)}
                className="icon-button"
                aria-label="Delete leave"
                title="Delete Leave"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {canCancel && (
              <button
                type="button"
                onClick={() => handleCancelLeave(leave)}
                className="icon-button text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                aria-label={`${cancelVerb} leave`}
                title={cancelVerb === 'Withdraw' ? 'Withdraw Application' : 'Cancel Leave (restore balance)'}
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Calculate leave statistics
  const leaveStats = {
    totalApplications: leaveApplications.length,
    pendingApplications: leaveApplications.filter(app => app.status === 'pending').length,
    approvedApplications: leaveApplications.filter(app => app.status === 'approved').length,
    autoApprovedApplications: leaveApplications.filter(app => app.status === 'auto_approved').length,
    availableLeaveTypes: filteredLeaveTypes.length
  };

  const loading = leaveTypesLoading || applicationsLoading || balancesLoading;

  // Debug logging removed - issues resolved

  if (leaveTypesError) {
    return (
      <div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-center">
            <XCircle className="w-6 h-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-red-900">Unable to Load Leave Types</h3>
              <p className="text-red-700 mt-1">
                Failed to fetch your eligible leave types. Please refresh the page or contact IT support.
              </p>
              <details className="mt-3">
                <summary className="text-sm text-red-600 cursor-pointer">Technical Details</summary>
                <pre className="text-xs text-red-600 mt-2 bg-red-100 p-2 rounded overflow-auto">
                  {JSON.stringify(leaveTypesError, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
            {/* Header - Using preferred styling from other pages */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
        <div className="space-y-4">
          {/* Desktop Layout: Title and Button on same row */}
          <div className="hidden sm:flex sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-blue-800" aria-hidden="true" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>
              </div>
              <p className="text-sm text-gray-500 ml-12">Apply for leave, track applications, and view your leave balances</p>
            </div>
            {/* Apply for Leave Button - Desktop */}
            <div className="flex items-center">
              <button
                onClick={() => {
                  setEditingLeave(null);
                  setShowApplicationModal(true);
                }}
                disabled={leaveTypesLoading || filteredLeaveTypes.length === 0}
                className="btn btn-primary"
                title={leaveTypesLoading ? "Loading leave types..." : (filteredLeaveTypes.length === 0 ? "No leave types available for your profile" : "Apply for leave")}
              >
                <PenSquare className="w-4 h-4 mr-2" aria-hidden="true" />
                Apply for Leave
              </button>
            </div>
          </div>

                      {/* Mobile Layout: Title and Full-width Button */}
          <div className="sm:hidden space-y-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-blue-800" aria-hidden="true" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>
              </div>
              <p className="text-sm text-gray-500 ml-12">Apply for leave, track applications, and view your leave balances</p>
            </div>
            {/* Action Buttons - Mobile Full Width */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  setEditingLeave(null);
                  setShowApplicationModal(true);
                }}
                disabled={leaveTypesLoading || filteredLeaveTypes.length === 0}
                className="btn btn-primary w-full"
                title={leaveTypesLoading ? "Loading leave types..." : (filteredLeaveTypes.length === 0 ? "No leave types available for your profile" : "Apply for leave")}
              >
                <PenSquare className="w-4 h-4 mr-2" aria-hidden="true" />
                Apply for Leave
              </button>
            </div>
          </div>
        </div>
      </div>

      {activeOverseasLeave?.leave_id && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 sm:p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                <Plane className="h-5 w-5 text-blue-800" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-blue-900">
                  You&apos;re currently on {activeOverseasLeave.leave_type}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-blue-800">
                  Your active leave runs until {formatDate(activeOverseasLeave.end_date)}.
                  If you need to stay longer, continue from this leave record instead of starting a new application.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleOpenActiveOverseasLeave}
              className="btn btn-primary w-full flex-shrink-0 sm:w-auto"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Request Extension
            </button>
          </div>
        </div>
      )}

      <div id="annual-leave-planning">
        <AnnualLeavePlanningCallout plan={yearEndPlan} />
      </div>

      {/* Annual Leave Eligibility Alert — suppress for the low-balance (<0.5 day) case
         since it's a transient state that resolves on the next accrual */}
      {shouldUseUnpaidForAnnual && annualLeaveEligibilityReason !== 'Annual Leave balance is below the minimum 0.5-day increment.' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              Annual Leave is currently unavailable
            </p>
            <p className="text-sm text-amber-700 mt-0.5">
              {annualLeaveEligibilityReason.includes('3 months')
                ? 'You need a minimum of 3 months service to be eligible for paid Annual Leave. Applications will be submitted as Unpaid Leave until then.'
                : 'Your Annual Leave entitlement has been fully utilised. Applications will be submitted as Unpaid Leave.'}
            </p>
          </div>
        </div>
      )}

      {/* Leave Balances Section - Enhanced UX */}
      {displayBalances.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h2 className="text-subsection-header flex items-center gap-2">
              <Wallet className="w-5 h-5 text-blue-800" />
              Your Leave Balances
            </h2>
            <p className="text-sm text-gray-600 mt-1">Track your available leave days and plan ahead</p>
          </div>
          
          {/* Pre-compute shared 60-day medical leave pool (MOM: HL entitlement includes OSL) */}
          {(() => {
            const oslBalance = leaveBalances.find(b => b.leave_type?.leave_type_name === 'Outpatient Sick Leave');
            const hlBalance = leaveBalances.find(b => b.leave_type?.leave_type_name === 'Hospitalisation Leave');
            const ptUser = user?.employment_type === 'part_time';
            const medicalLeavePool = {
              total: ptUser
                ? (parseFloat(oslBalance?.accrued_days) || 0) + (parseFloat(hlBalance?.accrued_days) || 0)
                : (parseFloat(oslBalance?.entitled_days) || 0) + (parseFloat(hlBalance?.entitled_days) || 0),
              used: (parseFloat(oslBalance?.used_days) || 0) + (parseFloat(hlBalance?.used_days) || 0),
            };
            medicalLeavePool.remaining = Math.max(0, medicalLeavePool.total - medicalLeavePool.used);

            return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayBalances.map((balance) => {
              // Handle special leave types (WFH, External Activity, Unpaid Leave) that use usage_count instead of balance
              const leaveTypeName = balance.leave_type?.leave_type_name;
              const isUnpaidLeave = leaveTypeName === 'Unpaid Leave';
              const isSpecialType = balance.is_work_arrangement || balance.is_status_only || isUnpaidLeave;
              
              // Part-time detection: all leave types use accrual-based display
              const isPartTime = user?.employment_type === 'part_time';
              const isAccrualBased = balance.leave_type?.leave_type_name === 'Annual Leave' || isPartTime;

              let remaining, total, used, usagePercentage, remainingPercentage;
              
              if (isSpecialType) {
                // For WFH and External Activity, show usage count instead of balance
                const usageCount = balance.usage_count || balance.used_days || 0;
                remaining = null; // No balance concept
                total = null; // No total concept
                used = usageCount;
                usagePercentage = 0; // No percentage for unlimited types
                remainingPercentage = 0;
              } else {
                // Regular leave types with balance - USE DATABASE VALUES
                used = balance.used_days || 0; // Use database value, not calculated
                
                if (isAccrualBased) {
                  // Accrual-based: AL for everyone, ALL types for part-timers
                  // Backend remaining_days and total_available_days already account for accrued_days
                  remaining = Number(balance.remaining_days ?? 0);
                  total = Number(balance.total_available_days ?? balance.entitled_days ?? 0);
                  usagePercentage = total > 0 ? Math.round((balance.used_days / total) * 100) : 0;
                  remainingPercentage = total > 0 ? Math.round((remaining / total) * 100) : 0;
                } else {
                  // Other leave types use traditional logic
                  remaining = balance.remaining_days || balance.remaining_balance || 0;
                  // Use total_available_days (backend handles carry-forward logic correctly)
                  total = Number(balance.total_available_days ?? balance.entitled_days ?? balance.total_entitlement ?? 0);
                  usagePercentage = total > 0 ? Math.round((used / total) * 100) : 0;
                  remainingPercentage = total > 0 ? Math.round((remaining / total) * 100) : 0;
                }
              }
              
              const { icon: LeaveIcon, color } = getLeaveTypeIcon(balance.leave_type?.leave_type_name);
              
              // Get status color based on remaining days (for regular types) or usage (for special types)
              const getStatusColor = () => {
                if (isSpecialType) {
                  return 'text-blue-600'; // Blue for usage-based types
                }
                if (remainingPercentage >= 70) return 'text-green-600';
                if (remainingPercentage >= 30) return 'text-orange-600';
                return 'text-red-600';
              };
              
              const getProgressColor = () => {
                if (isSpecialType) {
                  return 'bg-blue-500'; // Blue for usage-based types
                }
                if (remainingPercentage >= 70) return 'bg-green-500';
                if (remainingPercentage >= 30) return 'bg-orange-500';
                return 'bg-red-500';
              };
              
              const isOilCard = leaveTypeName === 'Off in Lieu';

              return (
                <div key={balance.id} className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-md transition-shadow duration-200 flex flex-col h-full gap-3">
                  {/* Header with Icon and Type */}
                  <div className="flex items-start justify-between gap-2 mb-4">
                    <div className="flex items-start min-w-0">
                      <div className={`w-10 h-10 ${getLeaveTypeColorClasses(color).bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <LeaveIcon className={`w-5 h-5 ${getLeaveTypeColorClasses(color).text}`} />
                      </div>
                      <div className="ml-3 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 truncate min-w-0">{balance.leave_type?.leave_type_name}</h3>
                          {isPartTime && !isSpecialType && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700">
                              Accrual
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {isSpecialType ? null : (
                      <>
                        {/* "Extended" badge: shown for Home Leave when the
                           worker's calendar-year usage went past the 30-day
                           planning cap because HR approved an extension
                           during the trip (or, for legacy migrated trips,
                           because the pre-Feb-2026 system recorded a single
                           trip > 30 days). This is a NORMAL, expected state
                           — extensions are the legitimate mechanism for
                           emergencies. Uses InfoBadge (per DS — category
                           badges must NOT use inline Tailwind), with the
                           canonical "Growth/Activity" orange tone. See
                           design-system-reference.md §Badge & Status
                           Indicators and chat 953a519e (May 2026). */}
                        {parseFloat(remaining) < 0 && parseFloat(balance.entitled_days || 0) > 0 && balance.leave_type?.leave_type_name !== 'Unpaid Leave' && (
                          <InfoBadge color="orange" label="Extended" />
                        )}
                        {/* "Exhausted" badge: at cap, but not over. */}
                        {parseFloat(remaining) === 0 && parseFloat(balance.entitled_days || 0) > 0 && balance.leave_type?.leave_type_name !== 'Unpaid Leave' && (
                          <InfoBadge color="gray" label="Exhausted" />
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Main Numbers */}
                  <div className="flex items-baseline justify-between mb-3">
                    {isSpecialType ? (
                      <div>
                        <p className={`text-3xl font-bold ${getStatusColor()}`}>{used}</p>
                        <p className="text-sm text-gray-500">days used this year</p>
                      </div>
                    ) : (
                      <>
                        <div>
                          {/* Home Leave is a calendar-year counter (MOM-aligned).
                              Three states for the headline number:
                                * remaining > 0  → normal "X days available"
                                * remaining = 0  → "0 days available" (exhausted)
                                * remaining < 0  → past the 30-day planning
                                  cap because HR approved an extension during
                                  the trip. NOT a violation — it's the
                                  expected outcome of an emergency extension.
                                  Frame as informational ("X days extension
                                  granted"), amber not red. See chat
                                  953a519e (May 2026). */}
                          {leaveTypeName === 'Home Leave' && parseFloat(remaining) < 0 ? (
                            <>
                              <p className={`text-3xl font-bold text-orange-600`}>
                                +{formatLeaveDaysEmployee(Math.abs(parseFloat(remaining)))}
                              </p>
                              <p className="text-sm text-orange-700">days extension granted</p>
                            </>
                          ) : (
                            <>
                              <p className={`text-3xl font-bold ${getStatusColor()}`}>{formatLeaveDaysEmployee(remaining)}</p>
                              <p className="text-sm text-gray-500">
                                days available
                              </p>
                            </>
                          )}
                          {/* Home Leave specifically: clarify the 1 Jan reset
                              for both at-cap and extended workers, so they
                              don't think the budget is gone forever. */}
                          {leaveTypeName === 'Home Leave' && parseFloat(balance.entitled_days || 0) > 0 && parseFloat(remaining) > 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              Trips starting in {new Date().getFullYear() + 1} use that year's cap. You can apply now.
                            </p>
                          )}
                          {leaveTypeName === 'Home Leave' && parseFloat(balance.entitled_days || 0) > 0 && parseFloat(remaining) <= 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              Cap resets on 1 Jan {new Date().getFullYear() + 1}. You can apply for {new Date().getFullYear() + 1} now.
                            </p>
                          )}
                          {/* Pending-aware AL indicator: shows when submitted apps reserve part of the balance */}
                          {balance.leave_type?.leave_type_name === 'Annual Leave' && (() => {
                            const effectiveRemaining = parseFloat(eligibilityData?.eligibilityDetails?.annual_leave?.remaining_balance ?? remaining);
                            const pendingDays = Math.max(0, parseFloat(remaining) - effectiveRemaining);
                            if (pendingDays < 0.5) return null;
                            return (
                              <p className="text-xs text-orange-600 mt-0.5">
                                {formatLeaveDaysEmployee(pendingDays)} {pluralizeDays(pendingDays)} pending approval
                              </p>
                            );
                          })()}
                          {isAccrualBased && parseFloat(balance.accrued_days || 0) < parseFloat(balance.entitled_days || 0) && (() => {
                            const nextDate = getNextAccrualDate(user?.employment_start_date, isPartTime);
                            return nextDate ? (
                              <p className="text-xs text-gray-400 mt-0.5">
                                Next accrual: {formatAccrualDate(nextDate)}
                              </p>
                            ) : null;
                          })()}
                          {/* OIL auto-consumption — no Apply button on this card, so explain the mechanism clearly */}
                          {balance.leave_type?.leave_type_name === 'Off in Lieu' && parseFloat(remaining) > 0 && (
                            <div className="mt-2 px-2.5 py-1.5 bg-teal-50 border border-teal-200 rounded-lg">
                              <p className="text-xs text-teal-800">
                                Used automatically when you apply for Annual Leave (oldest expiry first)
                              </p>
                            </div>
                          )}
                          {/* Show unpaid indicator ONLY for employees with < 3 months service (excludes system accounts) */}
                          {parseFloat(balance.entitled_days || 0) === 0 && parseFloat(remaining) === 0 && 
                           (balance.leave_type?.leave_type_name?.includes('Sick') || balance.leave_type?.leave_type_name?.includes('Hospitalisation')) &&
                           !isSystemAccount(user) && 
                           getCompletedServiceMonths(user?.employment_start_date) < 3 && (
                            <div className="text-xs text-orange-600 mt-1 font-medium">
                              Not yet entitled (probation) - Will be processed as unpaid leave
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-gray-900">
                            {formatLeaveDaysEmployee(isAccrualBased ? balance.entitled_days : total)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {isAccrualBased
                              ? (isPartTime ? 'FT-equivalent cap' : 'annual entitlement')
                              : (balance.expiry_date
                                  ? 'total entitlement'
                                  : 'annual entitlement')}
                          </p>
                          {isAccrualBased && (
                            <p className="text-xs text-blue-600">
                              Earned: {formatLeaveDaysEmployee(balance.accrued_days || 0)} days
                            </p>
                          )}
                          {isAccrualBased && parseFloat(balance.carried_forward_days || 0) > 0 && (
                            <p className="text-xs text-green-600">
                              Brought forward: {formatLeaveDaysEmployee(balance.carried_forward_days)} days
                            </p>
                          )}
                          {(leaveTypeName === 'Outpatient Sick Leave' || leaveTypeName === 'Hospitalisation Leave') && medicalLeavePool.total > 0 && (
                            <p className="text-xs text-purple-600">
                              Medical leave: {formatLeaveDaysEmployee(medicalLeavePool.remaining)} of {formatLeaveDaysEmployee(medicalLeavePool.total)} remaining
                            </p>
                          )}
                          {/* OIL expiry - FIFO-aware */}
                          {leaveTypeName === 'Off in Lieu' && (() => {
                            const hint = buildOilExpiryHint(balance);
                            if (!hint) return null;
                            return (
                              <p className={`text-xs font-medium ${hint.isExpiringSoon ? 'text-orange-600' : 'text-teal-600'}`}>
                                {hint.text}
                              </p>
                            );
                          })()}
                        </div>
                      </>
                    )}
                  </div>

                  {leaveTypeName === 'Annual Leave' && (
                    <AnnualLeaveYearEndBalance plan={yearEndPlan} />
                  )}
                  
                  {/* Progress Bar - Only show for regular leave types */}
                  {!isSpecialType && (
                    <div className="space-y-2">
                      <div className="flex justify-end text-xs text-gray-600">
                        <span>Used: {formatLeaveDaysEmployee(used)} {pluralizeDays(used)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
                          style={{ width: `${calculateProgressBarWidth(balance, total, remainingPercentage)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  {/* Apply action footer (hidden for OIL — auto-consumed via AL) */}
                  {!isOilCard && ((remaining > 0) || 
                    (balance.leave_type?.leave_type_name === 'Unpaid Leave') ||
                    (balance.leave_type?.leave_type_name?.includes('Sick') || balance.leave_type?.leave_type_name?.includes('Hospitalisation'))
                  ) && (
                    <div className="card-action-footer flex-nowrap">
                      <button
                        onClick={() => {
                          const ltName = balance.leave_type?.leave_type_name || '';
                          const ltId = balance.leave_type?.id || balance.leave_type_id;

                          // Worker leave types that need multi-level approval tracking (only for workers)
                          const workerLeaveTypes = ['Home Leave', 'Annual Leave', 'Urgent Leave'];
                          const isWorker = user?.employment_classification === 'worker';

                          if (workerLeaveTypes.includes(ltName) && isWorker) {
                            setWorkerLeaveType(ltName === 'Home Leave' ? 'home_leave' : ltName === 'Urgent Leave' ? 'urgent_leave' : 'annual_leave');
                            setSelectedWorkerLeave(null);
                            setWorkerLeaveInitialDates((ltName === 'Home Leave' || ltName === 'Urgent Leave') ? { start: '', end: '' } : null);
                            setShowWorkerLeaveModal(true);
                          } else {
                            setEditingLeave({
                              leave_type_id: ltId,
                              leave_type_name: ltName
                            });
                            setShowApplicationModal(true);
                          }
                        }}
                        className="btn btn-primary"
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
            );
          })()}
        </div>
      )}



      {/* Search and Filters — only relevant for the Applications view.
          The Transactions / statement view has its own inline year picker. */}
      {leaveTabMode === 'applications' && (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between space-x-3">
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder="Search leave applications..."
            theme="employee"
            className="flex-1 max-w-xl"
          />
          <div className="flex items-center space-x-3">
            <FilterButton
              theme="employee"
              isOpen={showFilters}
              activeCount={(selectedStatus ? 1 : 0) + (selectedLeaveType ? 1 : 0) + (selectedYear !== new Date().getFullYear() ? 1 : 0)}
              onClick={() => setShowFilters(!showFilters)}
            />
            
            {/* View Mode Toggle */}
            <PillToggle
              value={viewMode}
              onChange={setViewMode}
              options={[
                { id: 'grid', label: 'Grid view', icon: Grid3X3 },
                { id: 'list', label: 'List view', icon: List },
              ]}
              variant="icon"
              theme="employee"
              ariaLabel="View mode selection"
            />
            
            {(searchTerm || selectedStatus || selectedLeaveType || selectedYear !== new Date().getFullYear()) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedStatus('');
                  setSelectedLeaveType('');
                  setSelectedYear(new Date().getFullYear());
                  setCurrentPage(1);
                  setShowFilters(false);
                }}
                className="icon-button"
                title="Clear all filters"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <label className="form-label">Year</label>
                <PillSelect
                  value={selectedYear ? String(selectedYear) : ''}
                  onChange={(val) => { setSelectedYear(val ? Number(val) : ''); setCurrentPage(1); }}
                  options={[
                    { value: '', label: 'All Years' },
                    ...(() => {
                      const currentYear = new Date().getFullYear();
                      const years = [];
                      for (let y = currentYear + 1; y >= currentYear - 4; y--) {
                        years.push({ value: String(y), label: String(y) });
                      }
                      return years;
                    })()
                  ]}
                  theme="employee"
                  className="w-full"
                />
              </div>
              <div>
                <label className="form-label">Status</label>
                <PillSelect
                  value={selectedStatus}
                  onChange={(val) => { setSelectedStatus(val); setCurrentPage(1); }}
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'auto_approved', label: 'Auto Approved' },
                    { value: 'rejected', label: 'Rejected' },
                    { value: 'cancelled', label: 'Cancelled' },
                  ]}
                  theme="employee"
                  className="w-full"
                />
              </div>
              <div>
                <label className="form-label">Leave Type</label>
                <PillSelect
                  value={selectedLeaveType}
                  onChange={(val) => { setSelectedLeaveType(val); setCurrentPage(1); }}
                  options={[
                    { value: '', label: 'All Leave Types' },
                    ...availableLeaveTypes.map(type => ({
                      value: String(type.id),
                      label: type.name || type.leave_type_name,
                    }))
                  ]}
                  theme="employee"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Leave Applications / Transactions */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-2xl flex items-center justify-between gap-2 sm:gap-3">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center min-w-0 flex-shrink">
            <span className="truncate">{leaveTabMode === 'applications' ? 'Leave Applications' : 'Transactions'}</span>
            {leaveTabMode === 'applications' && (
              <CountBadge count={totalApplicationsCount} theme="employee" size="md" max={9999} className="ml-2 sm:ml-3 flex-shrink-0" />
            )}
          </h2>
          <PillToggle
            value={leaveTabMode}
            onChange={setLeaveTabMode}
            theme="employee"
            variant="icon"
            ariaLabel="Switch between leave applications and transactions"
            options={[
              { id: 'applications', label: 'Applications', icon: ClipboardList },
              { id: 'transactions', label: 'Transactions', icon: ArrowLeftRight },
            ]}
          />
        </div>

        {leaveTabMode === 'transactions' ? (
          <LeaveTransactionsView
            accounts={transactionAccounts}
            applications={transactionApplications}
            year={txYear}
            onYearChange={(y) => { setSelectedYear(y); setCurrentPage(1); }}
            user={user}
            loading={transactionAppsLoading || transactionBalancesLoading}
            getLeaveTypeIcon={getLeaveTypeIcon}
            getLeaveTypeColorClasses={getLeaveTypeColorClasses}
          />
        ) : loading ? (
          <div className="p-8 text-center">
            <LoadingSpinner size="lg" className="mx-auto" />
            <p className="text-gray-600 mt-3">Loading leave applications...</p>
          </div>
        ) : leaveApplications.length > 0 ? (
          viewMode === 'grid' ? (
            // Grid View
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {leaveApplications.map((leave) => renderLeaveCard(leave))}
              </div>
            </div>
          ) : (
            // List View (Table on desktop, cards on mobile)
            <>
            <div className="md:hidden p-4 space-y-3">
              {leaveApplications.map((leave) => renderLeaveCard(leave))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
              <thead className="table-header border-b border-gray-200">
                <tr>
                  <th className="table-header-cell">
                    Leave Type
                  </th>
                  <th className="table-header-cell">
                    Dates
                  </th>
                  <th className="table-header-cell">
                    Duration
                  </th>
                  <th className="table-header-cell">
                    Status
                  </th>
                  <th className="table-header-cell">
                    Submitted / Updated
                  </th>
                  <th className="table-header-cell text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {leaveApplications.map((leave) => (
                  <tr key={leave.id} className="table-row">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const ltName = getDisplayLeaveTypeName(leave);
                        const appliedName = getAppliedLeaveTypeName(leave);
                        const redirectedToUnpaid = appliedName === 'Annual Leave' && ltName === 'Unpaid Leave';
                        const displayName = redirectedToUnpaid ? 'Annual Leave' : ltName;
                        const { icon: RowIcon, color: rowColor } = getLeaveTypeIcon(displayName);
                        const rowClasses = getLeaveTypeColorClasses(rowColor);
                        return (
                          <div>
                            <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${rowClasses.bg} flex-shrink-0`}>
                                <RowIcon className={`w-3.5 h-3.5 ${rowClasses.text}`} />
                              </span>
                              {displayName}
                            </div>
                            {redirectedToUnpaid && (
                              <p className="text-xs text-amber-600 ml-8 mt-0.5">Deducted as Unpaid Leave</p>
                            )}
                          </div>
                        );
                      })()}
                      {(() => {
                        const leaveTypeName = (leave.leave_type?.leave_type_name || leave.leave_type_name || '').toLowerCase();
                        const isExternalActivity = leaveTypeName === 'external activity';
                        
                        if (isExternalActivity) {
                          if (leave.activity_type || leave.activity_location) {
                            return (
                              <div className="text-sm text-gray-500 space-y-0.5">
                                {leave.activity_type && (
                                  <div className="flex items-center gap-1 truncate">
                                    <Tag className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">{leave.activity_type}</span>
                                  </div>
                                )}
                                {leave.activity_location && (
                                  <div className="flex items-center gap-1 truncate">
                                    <MapPinned className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">{leave.activity_location}</span>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          if (leave.leave_reason) {
                            return (
                              <div className="text-sm text-gray-500 truncate">{leave.leave_reason}</div>
                            );
                          }
                        }
                        
                        const isOnCourse = leaveTypeName === 'on course';
                        if (isOnCourse) {
                          const info = getCourseInfo(leave);
                          if (info) {
                            return (
                              <div className="text-sm text-gray-500 space-y-0.5">
                                {info.courseName && (
                                  <div className="flex items-center gap-1 truncate">
                                    <GraduationCap className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">{info.courseName}</span>
                                  </div>
                                )}
                                {info.courseLocation && (
                                  <div className="flex items-center gap-1 truncate">
                                    <MapPinned className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">{info.courseLocation}</span>
                                  </div>
                                )}
                              </div>
                            );
                          }
                        }
                        
                        const isWFHRow = leaveTypeName.includes('work from home') || leaveTypeName.includes('wfh');
                        if (isWFHRow) {
                          const wfhLabel = getWfhReasonLabel(leave);
                          if (wfhLabel) {
                            return (
                              <div className="text-sm text-gray-500">
                                <div className="flex items-center gap-1 truncate">
                                  <Home className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                  <span className="truncate">{wfhLabel}</span>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }

                        // Fallback to generic reason for other leave types
                        if (leave.leave_reason) {
                          return (
                            <div className="text-sm text-gray-500">{leave.leave_reason}</div>
                          );
                        }
                        
                        return null;
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        {leave.leave_start_date === leave.leave_end_date ? (
                          // Single day - only show AM/PM badge if it's actually a half day (0.5 days)
                          <div className="flex items-center text-sm text-gray-900 gap-1">
                            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0 self-center" />
                            <span>{formatDate(leave.leave_start_date)}</span>
                            {getDisplayTotalDays(leave) === 0.5 && (
                              <SessionPeriodChip session={leave.leave_start_time_period} />
                            )}
                          </div>
                        ) : (
                          // Multi-day - inline layout with smart date range
                          <div className="flex items-center gap-2 text-sm text-gray-900">
                            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div className="flex items-center flex-wrap gap-1">
                              {(() => {
                                const startYear = getYearInSingapore(leave.leave_start_date);
                                const endYear = getYearInSingapore(leave.leave_end_date);
                                const sameYear = startYear === endYear;
                                const displayChips = getLeavePeriodDisplayChips(leave);
                                const startChip = displayChips.start;
                                const endChip = displayChips.end;
                                
                                return (
                                  <>
                                    <span className="font-medium">
                                      {sameYear ? formatDateShort(leave.leave_start_date) : formatDate(leave.leave_start_date)}
                                    </span>
                                    <SessionPeriodChip session={startChip} />
                                    <span className="text-gray-400 mx-0.5">→</span>
                                    <span className="font-medium">{formatDate(leave.leave_end_date)}</span>
                                    <SessionPeriodChip session={endChip} />
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 flex-wrap text-sm text-gray-900">
                        {(() => {
                          const credited = getOilCreditedDays(leave);
                          if (credited != null) {
                            const oilStatus = resolveLeaveStatus(leave);
                            return (
                              <span className={getOilCreditDaysClassName(oilStatus)}>
                                {formatOilCreditDaysLabel(credited, oilStatus)}
                              </span>
                            );
                          }
                          const days = getDisplayTotalDays(leave);
                          return (
                            <span>
                              {formatLeaveDays(days)} {pluralizeDays(days)}
                            </span>
                          );
                        })()}
                        {hasCrossTypeConsumption(leave, getDisplayLeaveTypeName(leave)) && (
                          <ConsumptionBreakdown proposal={leave.leave_consumption_proposal} />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const s = resolveLeaveStatus(leave);
                        const lt = (leave.leave_type?.leave_type_name || leave.leave_type_name || '').toLowerCase();
                        const isSickLeave = lt.includes('outpatient') && lt.includes('sick') || lt.includes('hospitalisation');
                        const isVerifiedSick = isSickLeave && s === 'approved';
                        return (
                          <>
                            <StatusBadge status={s} {...(isVerifiedSick ? { label: 'Verified' } : {})} />
                            <FlightTicketIndicator leave={leave} />
                          </>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="min-w-0">
                        <div className="flex items-center text-sm text-gray-900">
                          <FileText className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            {leave.leave_applied_at ? (
                              <>
                                <div className="text-xs text-gray-900 truncate">{formatDate(leave.leave_applied_at)}</div>
                                <div className="text-xs text-gray-500 truncate">
                                  {(() => {
                                    try {
                                      const date = new Date(leave.leave_applied_at);
                                      if (isNaN(date.getTime())) return 'Invalid date';
                                      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                    } catch (error) {
                                      return 'Invalid date';
                                    }
                                  })()}
                                </div>
                              </>
                            ) : '—'}
                          </div>
                        </div>
                        {resolveLeaveStatus(leave) === 'cancelled' && leave.cancelled_at ? (
                          <div className="flex items-center text-sm text-gray-900 mt-1">
                            <XCircle className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="text-xs text-gray-900 truncate">{formatDate(leave.cancelled_at)}</div>
                              <div className="text-xs text-gray-500 truncate">
                                {(() => {
                                  try {
                                    const date = new Date(leave.cancelled_at);
                                    if (isNaN(date.getTime())) return 'Invalid date';
                                    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                  } catch (error) {
                                    return 'Invalid date';
                                  }
                                })()}
                              </div>
                            </div>
                          </div>
                        ) : leave.leave_approved_at && resolveLeaveStatus(leave) !== 'cancelled' ? (
                          <div className="flex items-center text-sm text-gray-900 mt-1">
                            <CheckCircle className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="text-xs text-gray-900 truncate">{formatDate(leave.leave_approved_at)}</div>
                              <div className="text-xs text-gray-500 truncate">
                                {(() => {
                                  try {
                                    const date = new Date(leave.leave_approved_at);
                                    if (isNaN(date.getTime())) return 'Invalid date';
                                    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                  } catch (error) {
                                    return 'Invalid date';
                                  }
                                })()}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        {/* View Details - Always available */}
                        <button
                          onClick={() => handleViewDetails(leave)}
                          className="icon-button"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {/* Edit, Delete, Cancel - Conditional based on status */}
                        {(() => {
                          const status = resolveLeaveStatus(leave);
                          const lt = (leave.leave_type?.leave_type_name || leave.leave_type_name || '').toLowerCase();
                          const isSickLeave = lt.includes('outpatient') && lt.includes('sick') || lt.includes('hospitalisation');
                          const isVerifiedSick = isSickLeave && status === 'approved';
                          
                          // Hide all actions for verified sick/hospitalisation leave
                          if (isVerifiedSick) {
                            return null;
                          }
                          
                          // Use permission helpers for button visibility
                          const canDelete = canDeleteLeave(leave);
                          const canCancel = canCancelLeave(leave, todayStr);
                          const isDraft = status === 'draft' || status === 'draft_saved';
                          const canEdit = isDraft;
                          const cancelVerb = getCancelActionLabel(leave); // 'Withdraw' | 'Cancel'
                          
                          return (
                            <>
                              {canEdit && (
                                <button
                                  onClick={() => handleEditLeave(leave)}
                                  className="icon-button"
                                  title="Edit Leave"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  onClick={() => handleDeleteLeave(leave)}
                                  className="icon-button"
                                  title="Delete Leave"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                              {canCancel && (
                                <button
                                  onClick={() => handleCancelLeave(leave)}
                                  className="icon-button text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                  aria-label={`${cancelVerb} leave`}
                                  title={cancelVerb === 'Withdraw' ? 'Withdraw Application' : 'Cancel Leave (restore balance)'}
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            </>
          )
        ) : (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No leave applications</h3>
            <p className="text-sm text-gray-500 mb-4">You haven't submitted any leave applications yet.</p>
            <button
              type="button"
              onClick={() => {
                setEditingLeave(null);
                setShowApplicationModal(true);
              }}
              disabled={leaveTypesLoading || filteredLeaveTypes.length === 0}
              className="btn btn-primary"
            >
              <PenSquare className="w-4 h-4 mr-2" aria-hidden="true" />
              Apply for Leave
            </button>
          </div>
        )}

        {leaveTabMode === 'applications' && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalCount={totalApplicationsCount}
            pageSize={pageSize}
            theme="employee"
            noun="applications"
          />
        )}
      </div>

      {/* Leave Application Modal */}
      <SmartLeaveApplicationModal
        isOpen={showApplicationModal}
        onClose={() => {
          setShowApplicationModal(false);
          setEditingLeave(null);
        }}
        availableLeaveTypes={filteredLeaveTypes}
        isLoading={leaveTypesLoading}
        onSubmit={handleSubmitLeave}
        editingLeave={editingLeave}
        // Only route to WorkerLeaveModal if user is classified as 'worker' (foreign workers)
        // Regular staff use SmartLeaveApplicationModal with single-level approval
        onSwitchToWorkerLeave={
          user?.employment_classification === 'worker'
            ? handleSwitchToWorkerLeave
            : undefined
        }
      />

      {/* Worker Leave Modal (Home Leave, Annual Leave & Urgent Leave) */}
      <WorkerLeaveModal
        isOpen={showWorkerLeaveModal}
        onClose={() => {
          setShowWorkerLeaveModal(false);
          setSelectedWorkerLeave(null);
          setWorkerLeaveInitialDates(null);
          setWorkerLeaveType(null);
        }}
        leaveType={workerLeaveType}
        preselectedLeave={selectedWorkerLeave}
        initialDates={workerLeaveInitialDates}
        onApplicationCreated={(created) => {
          const startValue = created?.leave_start_date;
          const startYear = startValue
            ? Number(String(startValue).slice(0, 4))
            : null;
          if (Number.isFinite(startYear)) {
            setSelectedYear(startYear);
            setCurrentPage(1);
          }
          queryClient.invalidateQueries({ queryKey: ['leave'] });
        }}
      />

      {/* Leave Details Modal */}
      {showDetailsModal && selectedLeave && (
        <Modal
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          title="Leave Application Details"
          size="md"
          theme="employee"
          footer={(
            <button
              onClick={() => setShowDetailsModal(false)}
              className="btn btn-primary"
            >
              Close
            </button>
          )}
        >
              <div className="space-y-4">
                {/* Leave Type and Status */}
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 min-w-0 truncate flex-1">
                    {selectedLeave.leave_type?.leave_type_name || selectedLeave.leave_type_name}
                  </h4>
                  {(() => {
                    const status = resolveLeaveStatus(selectedLeave);
                    const leaveTypeName = (selectedLeave.leave_type?.leave_type_name || selectedLeave.leave_type_name || '').toLowerCase();
                    const isSickLeave = leaveTypeName.includes('outpatient') && leaveTypeName.includes('sick') || leaveTypeName.includes('hospitalisation');
                    const isVerifiedSick = isSickLeave && status === 'approved';
                    return (
                      <StatusBadge status={status} {...(isVerifiedSick ? { label: 'Verified' } : {})} size="md" />
                    );
                  })()}
                </div>

                {/* Date + Duration side-by-side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Date</label>
                    <div className="mt-1">
                      <LeavePeriodDisplay
                        application={selectedLeave}
                        totalDays={getDisplayTotalDays(selectedLeave)}
                        showIcon
                        size="sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Duration</label>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <Clock className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      <span className="text-sm text-gray-900">
                        {(() => {
                          const days = getDisplayTotalDays(selectedLeave);
                          return `${formatLeaveDays(days)} ${pluralizeDays(days)}`;
                        })()}
                      </span>
                      {selectedLeave.leave_consumption_proposal?.items?.length > 1 && (
                        <ConsumptionBreakdown
                          proposal={selectedLeave.leave_consumption_proposal}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Context-aware consumption warning banner */}
                {(() => {
                  const items = selectedLeave.leave_consumption_proposal?.items;
                  if (!items?.length) return null;
                  const ulItem = items.find(i => i.leave_type === 'Unpaid Leave' && parseFloat(i.days) > 0);
                  const hlItem = items.find(i => i.leave_type === 'Home Leave' && parseFloat(i.days) > 0);

                  if (ulItem) {
                    const ulDays = parseFloat(ulItem.days);
                    const detailStatus = resolveLeaveStatus(selectedLeave);
                    const isApprovedUnpaid = detailStatus === 'approved' || detailStatus === 'auto_approved';
                    const detailLt = (selectedLeave.leave_type?.leave_type_name || selectedLeave.leave_type_name || '').toLowerCase();
                    const isSickUnpaid = (detailLt.includes('outpatient') && detailLt.includes('sick')) || detailLt.includes('hospitalisation');
                    return (
                      <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-semibold">
                            {formatLeaveDays(ulDays)} {pluralizeDays(ulDays)} {isApprovedUnpaid ? 'recorded as Unpaid Leave' : 'will be Unpaid Leave'}
                          </span>
                          {isSickUnpaid && isApprovedUnpaid
                            ? ' — your paid sick leave was exhausted, so this reduces your take-home pay for the affected pay period.'
                            : isApprovedUnpaid
                              ? ' — this reduces your salary for the affected pay period.'
                              : ' — this may reduce your salary for the affected pay period.'}
                        </span>
                      </div>
                    );
                  }
                  if (hlItem) {
                    const alItem = items.find(i => i.leave_type === 'Annual Leave');
                    const alDays = alItem ? parseFloat(alItem.days) : 0;
                    const hlDays = parseFloat(hlItem.days);
                    return (
                      <HomeLeaveConsumptionNote
                        alDays={alDays}
                        hlDays={hlDays}
                        audience="employee"
                      />
                    );
                  }
                  return null;
                })()}

                {/* Reason / Activity Information */}
                {(() => {
                  const leaveTypeName = (selectedLeave.leave_type?.leave_type_name || selectedLeave.leave_type_name || '').toLowerCase();
                  const isExternalActivity = leaveTypeName === 'external activity';
                  
                  if (isExternalActivity) {
                    return (
                      <div className="space-y-4">
                        {/* Activity Type */}
                        {selectedLeave.activity_type && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Activity Type</label>
                            <div className="mt-1">
                              <p className="text-sm text-gray-900">{selectedLeave.activity_type}</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Activity Location */}
                        {selectedLeave.activity_location && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Activity Location</label>
                            <div className="mt-1">
                              <p className="text-sm text-gray-900">{selectedLeave.activity_location}</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Business Justification */}
                        {selectedLeave.business_justification && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Business Justification</label>
                            <div className="mt-1">
                              <p className="text-sm text-gray-900">{selectedLeave.business_justification}</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Project Code */}
                        {selectedLeave.project_code && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Project Code</label>
                            <div className="mt-1">
                              <p className="text-sm text-gray-900">{selectedLeave.project_code}</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Fallback to generic reason if no activity info */}
                        {!selectedLeave.activity_type && !selectedLeave.activity_location && !selectedLeave.business_justification && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Reason</label>
                            <div className="mt-1">
                              <p className="text-sm text-gray-900">
                                {selectedLeave.leave_reason || 'No reason provided'}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  } else if (leaveTypeName === 'on course') {
                    const courseInfo = getCourseInfo(selectedLeave);
                    return (
                      <div className="space-y-4">
                        {courseInfo?.courseName && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Course / Training Name</label>
                            <div className="mt-1">
                              <div className="flex items-center gap-1.5">
                                <GraduationCap className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <p className="text-sm text-gray-900">{courseInfo.courseName}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        {courseInfo?.courseLocation && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Course Location</label>
                            <div className="mt-1 flex items-center gap-1.5">
                              <MapPinned className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <p className="text-sm text-gray-900">{courseInfo.courseLocation}</p>
                            </div>
                          </div>
                        )}
                        {!courseInfo && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Reason</label>
                            <div className="mt-1">
                              <p className="text-sm text-gray-400 italic">No course details provided</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  } else if (leaveTypeName === 'claim: off in lieu') {
                    return (
                      <div className="space-y-3">
                        {(selectedLeave.oil_work_description || selectedLeave.leave_reason) && (
                          <div>
                            <label className="field-label">Work description</label>
                            <p className="text-sm text-gray-900">
                              {selectedLeave.oil_work_description || selectedLeave.leave_reason}
                            </p>
                          </div>
                        )}
                        {selectedLeave.oil_project_code && (
                          <div>
                            <label className="field-label">Project</label>
                            <p className="text-sm text-gray-900">
                              {selectedLeave.oil_project_name
                                ? `${selectedLeave.oil_project_name} `
                                : ''}
                              <span className="font-mono text-gray-500">
                                ({selectedLeave.oil_project_code})
                              </span>
                            </p>
                          </div>
                        )}
                        <OilClaimWorkDates oilDates={selectedLeave.oil_dates} />
                      </div>
                    );
                  } else if (leaveTypeName.includes('work from home') || leaveTypeName.includes('wfh')) {
                    const wfhReason = getWfhReasonLabel(selectedLeave);
                    return (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Reason</label>
                        <div className="mt-1 flex items-center gap-1.5">
                          <Home className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <p className="text-sm text-gray-900">{wfhReason || 'No reason provided'}</p>
                        </div>
                      </div>
                    );
                  } else {
                    // Sick leave doesn't require a reason (the MC is the
                    // supporting document) — only show it when one was given.
                    const isSickLeaveType = leaveTypeName === 'outpatient sick leave' || leaveTypeName === 'hospitalisation leave';
                    if (isSickLeaveType && !selectedLeave.leave_reason) {
                      return null;
                    }
                    return (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Reason</label>
                        <div className="mt-1">
                          <p className="text-sm text-gray-900">
                            {selectedLeave.leave_reason || 'No reason provided'}
                          </p>
                        </div>
                      </div>
                    );
                  }
                })()}

                <OverseasTravelDeclarationSummary application={selectedLeave} audience="employee" />

                <ApprovalStepper leave={selectedLeave} />

                <ApprovalTimeline leave={selectedLeave} />
              </div>
        </Modal>
      )}

      {/* Delete (draft) / Withdraw (submitted) / Cancel (approved) Confirmation Modal */}
      {(() => {
        const cancelVerb = confirmAction?.leave ? getCancelActionLabel(confirmAction.leave) : 'Cancel';
        const wasApproved = confirmAction?.leave ? isFullyApproved(confirmAction.leave) : false;
        const leaveTypeLabel = confirmAction?.leave?.leave_type?.leave_type_name || 'leave';
        const leaveDate = formatDate(confirmAction?.leave?.leave_start_date);

        let title = 'Delete Leave Application';
        let confirmLabel = 'Delete';
        if (confirmAction?.type === 'cancel') {
          title = cancelVerb === 'Withdraw' ? 'Withdraw Leave Application' : 'Cancel Leave Application';
          confirmLabel = cancelVerb === 'Withdraw' ? 'Withdraw' : 'Cancel Leave';
        }

        return (
          <ConfirmModal
            isOpen={!!confirmAction}
            title={title}
            confirmLabel={confirmLabel}
            variant="danger"
            onConfirm={handleConfirmAction}
            onCancel={() => { setConfirmAction(null); setCancellationReason(''); }}
            isLoading={deleteLeaveMutation.isPending || cancelLeaveMutation.isPending}
            theme="employee"
          >
            {confirmAction?.type === 'delete' ? (
              <p className="text-sm text-gray-700">
                Are you sure you want to delete this {leaveTypeLabel} draft for {leaveDate}? This draft has not been submitted and will be permanently removed.
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-700">
                  {cancelVerb === 'Withdraw'
                    ? `Are you sure you want to withdraw this ${leaveTypeLabel} application for ${leaveDate}? It will be retracted from approval and kept on record as withdrawn.`
                    : `Are you sure you want to cancel this ${leaveTypeLabel} application for ${leaveDate}?${wasApproved ? ' Your leave balance will be restored.' : ''}`}
                </p>
                <div className="mt-3">
                  <label htmlFor="cancel-reason" className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for {cancelVerb === 'Withdraw' ? 'withdrawal' : 'cancellation'}
                  </label>
                  <textarea
                    id="cancel-reason"
                    rows={3}
                    className="textarea"
                    placeholder={`Enter reason for ${cancelVerb === 'Withdraw' ? 'withdrawal' : 'cancellation'}...`}
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                  />
                </div>
              </>
            )}
          </ConfirmModal>
        );
      })()}
    </div>
  );
};

export default Leave;
